import { useMemo } from 'react';

import { usePlayerAssetStatus } from '@cartwhl/3d/player';
import type { LoadingState } from '@cartwhl/ui/feature/GenericLoader';
import GenericLoader from '@cartwhl/ui/feature/GenericLoader';

import { useStore as useKeyPoseStore } from '../keyPoses';
import { useStore } from '../motionEditorStore';
import { useStore as useRepathingStore } from '../repathing/repathingStore';

export default function MotionEditorLoader() {
  const loadingState = useMotionEditorLoadingState();

  if (!loadingState) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center">
      <GenericLoader loadingState={loadingState} />
    </div>
  );
}

function useMotionEditorLoadingState(): LoadingState | undefined {
  const store = useStore();
  const keyPoseStore = useKeyPoseStore();
  const repathingStore = useRepathingStore();
  const { scene, characters, playback } = store.state;
  const { generation: keyPoseGeneration } = keyPoseStore.state;
  const { generation: repathingGeneration } = repathingStore.state;
  const { sceneActors } = playback;
  const { resolvingMotionCharacter } = characters;
  const playerAssetStatus = usePlayerAssetStatus();

  const activePlayerActorId = playerAssetStatus.activeActorId;
  const actorStatus = activePlayerActorId
    ? playerAssetStatus.byActorId[activePlayerActorId]
    : undefined;
  const sceneActorStatuses = sceneActors.map(
    (actor) => playerAssetStatus.byActorId[actor.referenceName],
  );
  const isSceneMode = scene.status !== 'initial';
  const isMeshLoading = isSceneMode
    ? sceneActors.length > 0 &&
      sceneActorStatuses.some((status) => status?.meshStatus !== 'ready')
    : actorStatus?.meshStatus === 'initial' ||
      actorStatus?.meshStatus === 'pending';
  const isAnimationLoading = isSceneMode
    ? sceneActors.length > 0 &&
      sceneActors.some(
        (actor, index) =>
          actor.bvhUrls.length > 0 &&
          sceneActorStatuses[index]?.animationStatus !== 'ready',
      )
    : actorStatus?.animationStatus === 'initial' ||
      actorStatus?.animationStatus === 'pending';

  return useMemo(() => {
    let text: string = '';

    if (scene.status === 'initial') {
      // No scene id in url
      if (isMeshLoading || isAnimationLoading) {
        text = 'Loading player…';
      }
    } else {
      // Scene id in url
      if (scene.status === 'pending') {
        text = 'Loading scene data…';
      } else if (resolvingMotionCharacter) {
        text = 'Loading character mesh…';
      } else if (isMeshLoading) {
        text = 'Loading character mesh…';
      } else if (isAnimationLoading) {
        text = 'Loading animation…';
      }
    }

    const activeGeneration =
      keyPoseGeneration.status === 'pending'
        ? keyPoseGeneration
        : repathingGeneration.status === 'pending'
          ? repathingGeneration
          : null;
    if (activeGeneration) {
      switch (activeGeneration.phase) {
        case 'queued':
          text = 'In queue...';
          break;
        case 'updating-timeline':
          text = 'Updating timeline…';
          break;
        default:
          text = 'Regenerating…';
      }
    }

    return text
      ? {
          type: 'PROCESSING',
          text,
        }
      : undefined;
  }, [
    isMeshLoading,
    isAnimationLoading,
    keyPoseGeneration.phase,
    keyPoseGeneration.status,
    repathingGeneration.phase,
    repathingGeneration.status,
    resolvingMotionCharacter,
    scene.status,
  ]);
}
